/** App-wide vocabulary and chrome: labels, navigation, preferences, tasks. */

export default {
	common: {
		color: {
			custom: "Eigene Farbe (Hex)",
			customHint:
				"Eine exakte Farbe, etwa für eine Marke. Sie passt sich dem dunklen Design nicht an.",
			base: "Basis",
			customSwatch: "Eigene Farbe {hex}",
		},
		appName: "Notitia",
		tagline: "Wissensintensive Prozesse modellieren. Gemeinsam durchführen.",
		/** Der Absatz unter dem Leitsatz der Startseite: was Notitia ist, für alle, die es noch nicht kennen. */
		lead: "Notitia ist das Workflow-System von LiveReader für wissensintensive Arbeit. Im Designer halten Fachleute fest, was ein Prozess wissen muss, welche Schritte er geht und welche Entscheidungen er trifft. Im Operator führen Teams ihn live und gemeinsam durch — mit Sprachverständnis und KI, wo sie helfen, und mit Menschen, die entscheiden, wo es darauf ankommt.",
		actions: {
			back: "Zurück",
			save: "Speichern",
			discard: "Verwerfen",
			cancel: "Abbrechen",
			create: "Erstellen",
			edit: "Bearbeiten",
			duplicate: "Duplizieren",
			delete: "Löschen",
			remove: "Entfernen",
			removeNamed: "{name} entfernen",
			add: "Hinzufügen",
			close: "Schließen",
			undo: "Rückgängig",
			clear: "Zurücksetzen",
			more: "Weitere Aktionen",
			moreFor: "Weitere Aktionen für {name}",
		},
		viewState: {
			restored: "Filter wiederhergestellt",
		},
		fields: {
			email: "E-Mail",
			description: "Beschreibung",
			label: "Bezeichnung",
			displayName: "Anzeigename",
			language: "Sprache",
		},
		sidebar: {
			resize: "Breite dieses Panels ändern",
			expand: "Dieses Panel ausklappen",
			collapse: "Dieses Panel einklappen",
			close: "Dieses Panel schließen",
			dockLeft: "Seitenleiste links andocken",
			dockRight: "Seitenleiste rechts andocken",
			flip: "Seitenleiste auf die andere Seite verschieben",
		},
		keys: {
			ctrl: "Strg",
		},
		shortcuts: {
			pageSearch: "Diese Seite durchsuchen",
		},
		unknownUser: "Unbekannter Benutzer",
		toasts: {
			label: "Benachrichtigungen",
		},
		errors: {
			dataobjectInUse:
				"Etwas im Modell baut noch auf diesem Datenobjekt auf — ein Prozess verwendet es, oder ein Attribut hat es als Typ. Ändern Sie zuerst diese Stellen.",
			attributeInUseByAxiom:
				"Ein Axiom liest dieses Attribut noch, daher kann es nicht entfernt werden. Ändern oder entfernen Sie zuerst dieses Axiom.",
			dependencyProtected:
				"Das ist in einem anderen Projekt (einer Abhängigkeit) definiert und kann hier nicht geändert werden.",
			predefinedProtected: "Das ist von Notitia vordefiniert und kann nicht geändert werden.",
			actionManaged:
				"Diese Aktion gehört zu einer Frage. Was sie schreibt, ändern Sie an der Frage; hier kann sie nur verschoben, umbenannt und umgefärbt werden.",
			dependencyHeld:
				"Eine Abhängigkeit hat Änderungen, die auf Ihre Prüfung warten. Öffnen Sie die Abhängigkeitseinstellungen und lösen Sie die Abhängigkeit neu auf, bevor Sie Änderungen vornehmen, die Code neu erzeugen.",
			dataobjectInUseByAction:
				"Dieses Datenobjekt kann nicht gelöscht werden: Eine Aktion verwendet es noch. {reason}",
			relationInUseByAction:
				"Diese Beziehung kann nicht gelöscht werden: Eine Aktion verwendet sie noch. {reason}",
			processInUseByAction:
				"Dieser Prozess kann nicht gelöscht werden: Eine Aktion verweist noch auf ihn. {reason}",
			// Die vier Ablehnungen, die eine Liste von Stellen mitbringen: Der Dialog oder das Banner, das
			// gefragt hat, zeigt die Liste, daher sagen diese nur, was abgelehnt wurde. `{reason}` ist der
			// eigene Satz des Servers, der die Stellen ebenfalls nennt, und kommt nur dazu, wenn es keine
			// Liste zu zeigen gibt.
			attributeInUse: "Dieses Attribut kann nicht entfernt werden: Es wird noch verwendet.",
			typeInUse: "Dieser Typ kann nicht entfernt werden: Er wird noch verwendet.",
			typeValueInUse:
				"Dieser Wert kann nicht aus dem Typ entfernt werden: Er wird noch verwendet.",
			processDataobjectInUseByAction:
				"Dieser Prozesseintrag kann nicht entfernt werden: Eine Aktion erstellt daraus noch ein Datenobjekt.",
			notAnActor: "Das dürfen nur die Personen, für die es eingerichtet wurde.",
			userNotInDataspace:
				"Diese Person erreicht diesen Dataspace nicht, daher kann darin nichts für sie freigegeben werden.",
			projectImported:
				"Dieses Projekt wurde von einem anderen Server importiert: Seine Releases können hier laufen, aber darin lässt sich nichts bearbeiten.",
			invalidAlias:
				"Das ist kein gültiger Alias: Verwenden Sie Buchstaben a bis z, Ziffern und Bindestriche, höchstens 63 Zeichen, und nichts, was wie eine ID aussieht.",
			aliasTaken: "Dieser Alias ist bereits vergeben. Wählen Sie einen anderen.",
		},
		status: {
			saved: "Änderungen gespeichert.",
			loading: "Wird geladen…",
			noResults: "Keine Treffer",
			cannotUndo: "Das lässt sich nicht rückgängig machen.",
		},
		entities: {
			activities: "Aktivitäten",
			processes: "Prozesse",
			types: "Typen",
			dataobjects: "Datenobjekte",
			views: "Ansichten",
		},
	},
	nav: {
		signIn: "Anmelden",
		signOut: "Abmelden",
		account: "Konto",
		accountMenu: "Kontomenü von {name}",
		accountMenuPending:
			"Kontomenü von {name}, {n} offene Einladung | Kontomenü von {name}, {n} offene Einladungen",
		invitations: "Einladungen ({n})",
		home: "Projekte",
		designer: "Designer",
		operator: "Operator",
		"control-center": "Kontrollzentrum",
		projects: "Projekte",
		projectHome: "Projektübersicht",
		informationModel: "Informationsmodell",
		types: "Datentypen",
		terminology: "Terminologie",
		dataobjects: "Datenobjekte",
		processModeling: "Prozessmodellierung",
		processes: "Prozesse",
		activities: "Aktivitäten",
		dataspaces: "Dataspaces",
		departments: "Abteilungen",
		settings: "Einstellungen",
		expandRail: "Navigation ausklappen",
		collapseRail: "Navigation einklappen",
		documentation: "Dokumentation",
		clearViewState: "Ansichtszustand löschen",
	},
	preferences: {
		appearance: "Darstellung",
		panels: "Panels",
		autoCollapse: "Panels automatisch einklappen",
		keepPanelsOpen: "Panels offen lassen",
	},
	theme: {
		light: "Hell",
		dark: "Dunkel",
		system: "System",
	},
	languages: {
		en: "English",
		de: "Deutsch",
	},
	backgroundTasks: {
		title: "Hintergrundaufgaben",
	},
};
